import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useCallback, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useDispatch, useSelector } from 'react-redux'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import PasswordInputField from 'src/components/PasswordInputField/PasswordInputField'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { Environments } from 'src/configurations'
import { useCurrentBrand } from 'src/context/ConfigContext/useConfig'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { resetPasswordUrl, signupUrl } from 'src/navigation/urls'
import {
  postAuthType,
  resetVerificationErrors,
} from 'src/redux/actions/auth.actions'
import { selectEnvironmentFromState } from 'src/redux/selectors/session.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { normalizeEmail } from 'src/utils/string.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type SigninFormFlatFields = {
  email: string
  password: string
}
interface SigninFormProps {
  className?: string
  email: string
  setEmail: (val: string) => void
  password?: string
  setPassword: (val: string) => void
  postSignin: (email: string, password?: string) => void
  isSigningIn: boolean
  errorDetailMap?: ErrorDetailMap<string>
  topLevelError?: string
  didSessionExpireAutomatically: boolean
  isCsp?: boolean
}

const SigninForm: React.FC<SigninFormProps> = (props) => {
  const env = useSelector(selectEnvironmentFromState)
  const { APP_NAME: rawAppName } = useCurrentBrand()

  const dispatch = useDispatch()
  const translate = useTranslate()
  const appName = translate(rawAppName)

  const [recaptchaSuccess, setRecaptchaSuccess] = useState(
    env === Environments.Prod ? '' : 'not prod',
  )
  const sleep = (ms: number): Promise<number> =>
    new Promise((r) => setTimeout(r, ms))

  if (env === Environments.Prod) {
    const { executeRecaptcha } = useGoogleReCaptcha()

    const handleReCaptchaVerify = useCallback(async () => {
      if (!executeRecaptcha) {
        /* eslint-disable-next-line no-console */
        console.log('Execute recaptcha not yet available')
        return
      }
      await sleep(100)
      const token = await executeRecaptcha('yourAction')
      setRecaptchaSuccess(token)
    }, [executeRecaptcha])

    useEffect(() => {
      handleReCaptchaVerify()
    }, [handleReCaptchaVerify])
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    dispatch(resetVerificationErrors())
    if (recaptchaSuccess !== '' && flatFieldsSetErrors()) {
      const normalizedEmail = normalizeEmail(flatFields.email)
      // set redux email to flat field value for good measure
      props.setEmail(normalizedEmail)
      dispatch(
        postAuthType({
          data: normalizedEmail,
        }),
      )
      props.postSignin(normalizedEmail, flatFields.password)
    }
  }

  const flatFieldsValidation =
    (): FormFieldsValidation<SigninFormFlatFields> => ({
      email: [stringIsEmpty()],
      password: [stringIsEmpty()],
    })
  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<SigninFormFlatFields>(
    {
      email: '',
      password: '',
    },
    flatFieldsValidation,
    props.errorDetailMap,
  )

  return (
    <form
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
      onSubmit={onSubmit}
    >
      <span
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 40px;
        `}
      >
        {translate(props.isCsp ? 'Customer support' : 'Welcome to ${appName}', {
          appName,
        })}
      </span>
      <p
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          margin-bottom: 40px;
        `}
      >
        <Trns
          source={
            props.isCsp
              ? "You must have an existing ${appName} profile in order to log into your customer support account. Don't have an ${appName} profile? ${signUpLink}"
              : 'To log into an existing ${appName} profile, enter your email below. New to ${appName}? ${signUpLink}'
          }
          expressions={{
            signUpLink: (
              <HoverableLink
                id={'signUp'}
                underline
                size={HoverableLinkSizes.Large}
                to={props.isCsp ? undefined : signupUrl()}
                href={props.isCsp ? signupUrl() : undefined}
                ariaLabelSource={'Sign Up'}
              >
                {translate('Sign Up')}
              </HoverableLink>
            ),
            appName,
          }}
        />
      </p>
      {props.didSessionExpireAutomatically && (
        <div
          css={css`
            border-radius: 10px;
            padding: 20px;
            position: relative;
            margin-bottom: 40px;

            &:before {
              border-radius: 10px;
              content: '';
              background-color: ${themeConstants.colors.highlight};
              opacity: 0.25;
              position: absolute;
              top: 0;
              bottom: 0;
              left: 0;
              right: 0;
            }
          `}
        >
          <p
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <Trns
              source={
                '${yourSessionExpired}, and you have been automatically logged out. You will need to log back in to continue working.'
              }
              expressions={{
                yourSessionExpired: (
                  <span
                    css={css`
                      ${getFontCss(
                        12,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {translate('Your session expired')}
                  </span>
                ),
              }}
            />
          </p>
        </div>
      )}
      <TextInputField
        id={'email'}
        design={TextInputFieldDesigns.New}
        fieldLabel={translate('Email')}
        value={flatFields['email']}
        placeholder={translate('Enter a complete email address')}
        onChange={(val: string): void => {
          onFlatFieldChange('email', val)
          props.setEmail(val)
        }}
        type={'email'}
        autoComplete={'email'}
        errors={flatFieldsErrors['email'] || props.errorDetailMap?.['data']}
      />
      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <PasswordInputField
          id={'login'}
          data-testid="password-input"
          value={flatFields['password']}
          onChange={(val: string): void => {
            onFlatFieldChange('password', val)
            props.setPassword(val)
          }}
          errors={
            flatFieldsErrors['password'] || props.errorDetailMap?.['password']
          }
        />
      </div>
      {props.topLevelError && (
        <span
          data-testid="signInError"
          css={css`
            margin-top: 30px;
            align-self: center;
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {props.topLevelError}
        </span>
      )}
      <Button
        id={'loginBtn'}
        data-testid="login-button"
        type={ButtonTypes.submit}
        css={css`
          margin-top: 40px;
        `}
        design={ButtonDesigns.designCta}
        text={translate('Log in')}
        loading={props.isSigningIn}
        disabled={props.isSigningIn}
        big
      />
      <HoverableLink
        css={css`
          align-self: center;
          margin-top: 40px;
        `}
        underline
        to={props.isCsp ? undefined : resetPasswordUrl()}
        href={props.isCsp ? resetPasswordUrl() : undefined}
        size={HoverableLinkSizes.Large}
        ariaLabelSource={'Forgot your password?'}
      >
        {translate('Forgot your password?')}
      </HoverableLink>
    </form>
  )
}

export default SigninForm
